/**
 * Frontend Auth Service
 *
 * Typed wrapper around the backend /api/auth/* endpoints. All requests use
 * `credentials: 'include'` so the HttpOnly session_id cookie is sent.
 *
 * The frontend never touches Supabase auth directly — the backend manages
 * tokens server-side and the browser only ever sees the opaque session cookie.
 */

import { httpClient } from "../api/http-client";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  organizationId: string | null;
  emailVerified: boolean;
}

export interface AuthRole {
  name: string;
  isAdmin: boolean;
}

export interface AuthMeResponse {
  user: AuthUser;
  role: AuthRole;
  permissions: string[];
}

export type LoginResponse = AuthMeResponse;

export interface SignupResponse {
  user: { id: string; email: string; emailVerified: boolean };
  needsVerification: boolean;
}

export const authApi = {
  /** POST /api/auth/login — sets session cookie, returns user/role/permissions */
  login: (email: string, password: string) =>
    httpClient.post<LoginResponse>("/auth/login", { email, password }),

  /** POST /api/auth/signup */
  signup: (email: string, password: string, fullName?: string) =>
    httpClient.post<SignupResponse>("/auth/signup", {
      email,
      password,
      fullName,
    }),

  /** GET /api/auth/me — restore session on page load */
  getCurrentUser: () => httpClient.get<AuthMeResponse>("/auth/me"),

  /** GET /api/auth/session — quick validity check */
  getSession: () => httpClient.get<{ valid: boolean }>("/auth/session"),

  /** POST /api/auth/logout */
  logout: () => httpClient.post<{ success: boolean }>("/auth/logout"),

  /** POST /api/auth/reset-password */
  resetPassword: (email: string) =>
    httpClient.post<{ success: boolean; message: string }>(
      "/auth/reset-password",
      { email },
    ),

  /** POST /api/auth/accept-invite — complete a team invitation magic link */
  acceptInvite: (
    access_token: string,
    refresh_token: string,
    password: string,
  ) =>
    httpClient.post<AuthMeResponse>("/auth/accept-invite", {
      access_token,
      refresh_token,
      password,
    }),

  /** POST /api/auth/complete-onboarding — save first/last name + mark done */
  completeOnboarding: (first_name: string, last_name: string) =>
    httpClient.post<AuthMeResponse>("/auth/complete-onboarding", {
      first_name,
      last_name,
    }),

  /** GET /api/auth/permissions — force-refresh */
  getPermissions: () =>
    httpClient.get<{ permissions: string[] }>("/auth/permissions"),

  /** POST /api/auth/realtime-token — get Supabase access token for Realtime */
  getRealtimeToken: () =>
    httpClient.post<{ access_token: string }>("/auth/realtime-token"),

  /** GET /api/auth/realtime-config — Supabase URL + anon key */
  getRealtimeConfig: () =>
    httpClient.get<{ supabase_url: string; supabase_anon_key: string }>(
      "/auth/realtime-config",
    ),
};
